/**
 * Phase 24.4b Step 6 — single meal-plan item card.
 *
 * Stripe colors were dropped at Step 9 review: at /30-/40 opacity the
 * strawman was perceptually invisible against cream background, and
 * the uppercase amber-dark meal-type label already carries identity.
 * Adding decoration that doesn't add information is visual noise; if
 * future pre/post-workout slots benefit from differentiation, stripes
 * can be reintroduced with stronger contrast then.
 *
 * Phase 24.8a-frontend — when recipe.image_url is set, render h-24
 * thumbnail edge-to-edge above the text content; cards без image_url
 * unchanged (no placeholder for small cards — keeps grid clean until
 * the corpus-wide photo batch lands).
 */
import { useTranslation } from "react-i18next";
import { Check } from "lucide-react";
import type { MealPlanItemRead } from "@/api/meal_plan";
import { cn } from "@/lib/utils";

interface Props {
  item: MealPlanItemRead;
  onClick?: () => void;
}

export function MealCard({ item, onClick }: Props) {
  const { t } = useTranslation();
  const reasonText = item.selection_reason
    ? t(item.selection_reason.key, item.selection_reason.params as Record<string, string | number>)
    : null;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className={cn(
        "flex w-full flex-col overflow-hidden rounded-lg border border-cream-border bg-cream text-left transition",
        onClick ? "hover:border-amber/50 hover:shadow-soft" : "cursor-default",
      )}
      data-testid="meal-card"
      data-meal-type={item.meal_type}
    >
      {item.recipe.image_url ? (
        <img
          src={item.recipe.image_url}
          alt=""
          className="aspect-[4/3] w-full object-cover"
          loading="lazy"
          data-testid="meal-card-image"
        />
      ) : null}
      <div className="min-w-0 flex-1 p-3">
        <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-amber-dark">
          <span>{t(`meal_type.${item.meal_type}`)}</span>
          {item.is_cooked ? (
            <span
              className="inline-flex items-center gap-0.5 text-[10px] text-navy"
              title={t("meal_plan.cooked")}
            >
              <Check className="h-3 w-3" strokeWidth={2.5} />
              {t("meal_plan.cooked_short")}
            </span>
          ) : null}
        </div>
        <div className="mt-0.5 text-[13px] font-medium text-navy-deep">
          {item.recipe.name}
        </div>
        <div className="mt-1 text-[11px] text-taupe">
          <span className="metric-number text-navy-deep">
            {Math.round(item.planned_kcal)}
          </span>{" "}
          {t("nutrition_page.kcal")} · {t("nutrition_page.p")}{" "}
          <span className="metric-number">{Math.round(item.planned_protein_g)}</span> ·{" "}
          {t("nutrition_page.c")}{" "}
          <span className="metric-number">{Math.round(item.planned_carbs_g)}</span> ·{" "}
          {t("nutrition_page.f")}{" "}
          <span className="metric-number">{Math.round(item.planned_fat_g)}</span>
        </div>
        {reasonText ? (
          <div className="mt-1 text-[10px] italic text-taupe">{reasonText}</div>
        ) : null}
      </div>
    </button>
  );
}
